import { isFuture, isPast } from "date-fns"
import { useCallback, useMemo, useState } from "react"
import { trpc } from "../../../../api/trpc/trpc"
import { DistributionTariff } from "@nano-portal/shared"

export const useAnnualPriceController = () => {
  const [filter, setFilter] = useState({ future: true, past: true })
  const [annualConsumptionInMwh, setAnnualConsumptionInMwh] = useState<number | undefined>(2)
  const [distributionTariff, setDistributionTariff] = useState(DistributionTariff.D02d)

  const { isLoading, isError, data } = trpc.useQuery(["web.consumptionPrices", { distributionTariff }], {
    keepPreviousData: true,
  })

  const filterData = useCallback(
    (data?: Map<number, number>) => {
      const result: { marketPrice: number; month: Date }[] = []
      if (!data) {
        return result
      }

      for (const [month, price] of data) {
        if (filter.future && isFuture(month)) {
          result.push({ marketPrice: price, month: new Date(month) })
        }

        if (filter.past && isPast(month)) {
          result.push({ marketPrice: price, month: new Date(month) })
        }
      }

      return result
    },
    [filter]
  )

  const filteredData = useMemo(() => {
    return filterData(data?.energyPricesInMonths)
  }, [filterData, data?.energyPricesInMonths])

  const chartData = useMemo(() => {
    return filteredData.map((d) => ({ ...d, marketPrice: d.marketPrice * (annualConsumptionInMwh ?? 0) }))
  }, [filteredData, annualConsumptionInMwh])

  const futureSum = (data?.totalPriceInFuture ?? 0) * (annualConsumptionInMwh ?? 0)
  const pastSum = (data?.totalPriceInPast ?? 0) * (annualConsumptionInMwh ?? 0)
  const total = (data?.settlement ?? 0) * (annualConsumptionInMwh ?? 0)

  return {
    chartData,
    isError,
    isLoading,
    futureSum,
    pastSum,
    total,
    hidePastMonths: !filter.past,
    onPastLegendClick: () => setFilter((prev) => ({ ...prev, past: !prev.past })),
    hideFutureMonths: !filter.future,
    onFutureLegendClick: () => setFilter((prev) => ({ ...prev, future: !prev.future })),
    distributionTariff,
    setDistributionTariff,
    annualConsumptionInMwh,
    setAnnualConsumptionInMwh,
  }
}
